// FocusTimerStart.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList, faCog, faGlobe, faTimes } from '@fortawesome/free-solid-svg-icons';

import '../focustimer/FocusTimer.css';
import '../focustimer/Pomodoro.css';
import pomodoroData from '../focustimer/Pomodoro';

const gentlePianoMusic = process.env.PUBLIC_URL + '/assets/sounds/cheerful-whistling.mp3';


const SHEETALFocusTimerStart = () => {
  const navigate = useNavigate();
  const { authState } = useSelector((state) => ({ authState: state.authReducer }));
  const [tasks, setTasks] = useState([]);
  const [currentTaskTitle, setCurrentTaskTitle] = useState('');
  const [currentTaskId, setCurrentTaskId] = useState('');
  const [showTaskList, setShowTaskList] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [error, setError] = useState('');

  // Timer settings
  const [focusTime, setFocusTime] = useState(25);
  const [shortBreakDuration, setShortBreakDuration] = useState(5);
  const [longBreakDuration, setLongBreakDuration] = useState(25);
  const [shortBreakCount, setShortBreakCount] = useState(12);
  const [longBreakCount, setLongBreakCount] = useState(1);

  // Timer state
  const [initialTime, setInitialTime] = useState(focusTime);
  const [time, setTime] = useState(initialTime);
  const [timerOn, setTimerOn] = useState(false);
  const [showStopButtons, setShowStopButtons] = useState(false);
  const [disableButtons, setDisableButtons] = useState(false);
  const [isBreakTime, setIsBreakTime] = useState(false);

  const audioRef = useRef(new Audio(gentlePianoMusic));
  const intervalRef = useRef(null);

  const [fetchData, { loading }] = useFetch();

  // Fetch tasks
  const fetchTasks = useCallback(() => {
    if (!authState.isLoggedIn) return;

    const config = {
      url: '/tasks/myfocustimertasks',
      method: 'post',
      headers: { Authorization: authState.token },
      data: { userId: authState.user._id },
    };
    fetchData(config, { showSuccessToast: false }).then((data) => setTasks(data.tasks));
  }, [authState.isLoggedIn, authState.token, authState.user._id, fetchData]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Update initial time when focusTime changes
  useEffect(() => {
    setInitialTime(focusTime * 60); // Convert to seconds
  }, [focusTime]);

  // Timer interval
  useEffect(() => {
    if (timerOn) {
      intervalRef.current = setInterval(() => {
        setTime((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(intervalRef.current);
            setTimerOn(false);
            setIsBreakTime(true);
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
      audioRef.current.play().catch((e) => console.warn('Audio play failed:', e));
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (!isBreakTime) {
        audioRef.current.pause();
      }
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [timerOn, isBreakTime]);

  const handleShowTaskList = () => {
    if (tasks.length === 0) {
      fetchTasks();
    }
    setShowTaskList((prev) => !prev);
  };

  const handleTaskClick = (title, id) => {
    setCurrentTaskTitle(title);
    setCurrentTaskId(id);
    setShowTaskList(false);
    setStartTime(new Date());
  };

  const handleShowPreferences = () => {
    setShowPreferences((prev) => !prev);
  };

  const handleSavePreferences = () => {
    // Preferences are saved via state updates; no additional action needed
    setShowPreferences(false);
  };

  const startTimer = () => {
    setTime(initialTime);
    setTimerOn(true);
    setShowStopButtons(false);
    setDisableButtons(false);
    setIsBreakTime(false);
    setStartTime(new Date());
  };

  const pauseTimer = () => {
    setTimerOn(false);
  };

  const stopTimer = () => {
    setShowStopButtons(true);
    setTimerOn(false);
    setTime(initialTime);
    setDisableButtons(true);
    setIsBreakTime(false);
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
  };

  const handleBackToHome = () => {
    navigate('/sheearns');
  };

  const handleSaveData = () => {
    if (!currentTaskId || !startTime) {
      setError('No task selected or timer not started.');
      return;
    }

    const endTime = new Date();
    const timeSpent = endTime - startTime;

    const focusData = {
      userId: authState.user._id,
      taskId: currentTaskId,
      startTime,
      endTime,
      timeSpent,
      pomodoroTime: initialTime,
    };

    const config = {
      url: '/focustimer',
      method: 'post',
      data: { focusData },
      headers: { Authorization: authState.token },
    };

    // console.log('Posting Data', JSON.stringify(focusData, null, 2), 'User', authState.user._id);

    fetchData(config)
      .then(() => {
        navigate('/focus-timer-home');
      })
      .catch((err) => {
        console.error('Error saving focus timer:', err);
        setError('Error saving focus timer data.');
      });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const sortedTasks = [...tasks].sort((a, b) => new Date(b.taskDate) - new Date(a.taskDate));

  if (!authState.isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <p>Please log in to use the focus timer.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-4 px-2 sm:py-6 sm:px-4">
      {/* Header */}
      <h2 className="text-xl sm:text-2xl text-yellow-200 text-center mb-4 sm:mb-6">Focus Timer</h2>

      {/* Main Timer Container */}
      <div className="border border-green-500 mb-4 px-2 py-4 sm:px-4 sm:py-6 rounded-md">
        {/* Action Buttons */}
        <div className="flex justify-center space-x-2 mb-4">
          <button
            className="p-2 text-gray-300 hover:text-white transition-colors"
            onClick={handleShowTaskList}
            aria-label="Select Task"
          >
            <FontAwesomeIcon icon={faList} size="lg" />
          </button>
          <button
            className="p-2 text-gray-300 hover:text-white transition-colors"
            onClick={handleShowPreferences}
            aria-label="Settings"
          >
            <FontAwesomeIcon icon={faCog} size="lg" />
          </button>
          <button className="p-2 text-gray-400" disabled aria-label="Global View">
            <FontAwesomeIcon icon={faGlobe} size="lg" />
          </button>
          <button
            className="p-2 text-gray-300 hover:text-white transition-colors"
            onClick={handleBackToHome}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </button>
        </div>

        {/* Task Modals */}
        {showTaskList && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-md max-h-[80vh] overflow-y-auto bg-blue-100 text-black rounded-md p-4">
              <h3 className="text-lg font-semibold mb-4 text-center">Select Your Task to Focus On</h3>
              {sortedTasks.length > 0 ? (
                <ul className="space-y-2">
                  {sortedTasks.map((task, index) => (
                    <li
                      key={task._id}
                      className="border-b border-gray-300 pb-2 cursor-pointer hover:bg-blue-200 p-2 rounded"
                      onClick={() => handleTaskClick(task.title, task._id)}
                    >
                      <strong className="text-sm">{index + 1}.</strong> {task.title}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-center text-orange-500 text-lg mt-8">There are no tasks to focus on!</p>
              )}
              <button
                className="mt-4 w-full p-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                onClick={() => setShowTaskList(false)}
              >
                <FontAwesomeIcon icon={faTimes} /> Close
              </button>
            </div>
          </div>
        )}

        {showPreferences && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-md max-h-[80vh] overflow-y-auto bg-blue-100 text-black rounded-md p-4">
              <h3 className="text-lg font-semibold mb-4 text-center">Timer Preferences</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-lg mb-1">Focus Time (minutes)</label>
                  <input
                    type="number"
                    value={focusTime}
                    min="10"
                    max="115"
                    step="5"
                    onChange={(e) => setFocusTime(Number(e.target.value))}
                    className="w-full px-3 py-2 border rounded-md text-center"
                    placeholder="25"
                  />
                </div>
                <div>
                  <label className="block text-lg mb-1">Short Break (minutes)</label>
                  <input
                    type="number"
                    value={shortBreakDuration}
                    min="5"
                    max="10"
                    step="5"
                    onChange={(e) => setShortBreakDuration(Number(e.target.value))}
                    className="w-full px-3 py-2 border rounded-md text-center"
                    placeholder="5"
                  />
                </div>
                <div>
                  <label className="block text-lg mb-1">Max Short Breaks</label>
                  <input
                    type="number"
                    value={shortBreakCount}
                    min="1"
                    max="40"
                    step="5"
                    onChange={(e) => setShortBreakCount(Number(e.target.value))}
                    className="w-full px-3 py-2 border rounded-md text-center"
                    placeholder="12"
                  />
                </div>
                <div>
                  <label className="block text-lg mb-1">Long Break (minutes)</label>
                  <input
                    type="number"
                    value={longBreakDuration}
                    min="25"
                    max="120"
                    step="10"
                    onChange={(e) => setLongBreakDuration(Number(e.target.value))}
                    className="w-full px-3 py-2 border rounded-md text-center"
                    placeholder="25"
                  />
                </div>
                <div>
                  <label className="block text-lg mb-1">Max Long Breaks</label>
                  <input
                    type="number"
                    value={longBreakCount}
                    min="1"
                    max="40"
                    step="5"
                    onChange={(e) => setLongBreakCount(Number(e.target.value))}
                    className="w-full px-3 py-2 border rounded-md text-center"
                    placeholder="1"
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 mt-6">
                <button
                  className="flex-1 p-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                  onClick={handleSavePreferences}
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-2" /> Save
                </button>
                <button
                  className="flex-1 p-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                  onClick={() => setShowPreferences(false)}
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-2" /> Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Current Task */}
        {currentTaskTitle && (
          <div className="text-white text-center text-lg sm:text-xl mb-6">{currentTaskTitle}</div>
        )}

        {/* Timer Display */}
        <div className="timer text-center text-white text-4xl sm:text-5xl mb-8 font-mono">
          {formatTime(time)}
        </div>

        {/* Control Buttons */}
        <div className="grid grid-cols-3 gap-3 justify-center place-items-center">
          <button
            className="px-4 py-3 w-10 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 transition-colors flex items-center justify-center"
            onClick={startTimer}
            disabled={timerOn || showStopButtons || disableButtons}
          >
            ▶️
          </button>
          <button
            className="px-4 py-3 w-10 bg-yellow-200 text-white rounded-md hover:bg-yellow-700 disabled:bg-gray-400 transition-colors flex items-center justify-center"
            onClick={pauseTimer}
            disabled={!timerOn}
          >
            ⏸️
          </button>
          <button
            className="px-4 py-3 w-10 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 transition-colors flex items-center justify-center"
            onClick={stopTimer}
            disabled={!timerOn}
          >
            ⏹️
          </button>
        </div>

        {/* Stop Confirmation Buttons */}
        {showStopButtons && (
          <div className="mt-2 flex flex-row gap-2 justify-center">
            <button
              className="px-6 py-3 bg-orange-700 text-white rounded-md hover:bg-orange-800 transition-colors"
              onClick={handleSaveData}
            >
              Stop Early - 5 Whys Analysis
            </button>
            <button
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              onClick={handleBackToHome}
            >
              Back to Home
            </button>
          </div>
        )}

        {error && (
          <div className="mt-4 text-center text-red-400 text-sm">{error}</div>
        )}
      </div>

      {/* Break Suggestions */}
      <div className="border border-green-400 mb-4 px-2 py-4 sm:px-4 sm:py-6 rounded-md">
        <h3 className="text-lg text-white mb-4 text-center">Break Activity Suggestions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {pomodoroData.map((item, index) => (
            <div key={index} className="bg-gray-800 p-4 rounded-md">
              <h4 className="text-white font-semibold mb-3">{item.type}</h4>
              <ul className="space-y-2">
                {item.activities.map((activity, activityIndex) => (
                  <li key={activityIndex} className="flex items-center text-sm">
                    <span className={`mr-3 text-${activityIndex % 2 === 0 ? 'blue' : 'gray'}-400`}>
                      <i className={activity.icon} aria-label={activity.label} />
                    </span>
                    <span className="text-gray-300">{activity.activity}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SHEETALFocusTimerStart;